import type { IconName } from '../components/icons.generated';
import type { ImageName } from '../generated/images';

export type ServiceFact = { label: string; value: string };
export type ServiceFaq = { question: string; answer: string };

export type Service = {
  slug: string;
  /** Short name for nav, footer and breadcrumbs. */
  name: string;
  icon: IconName;
  /** Homepage "Comprehensive Architectural Interior Solutions" card. Only services with
   *  a homeCard appear there (in array order). */
  homeCard?: {
    badge: string;
    title: string;
    description: string;
    highlights: string[];
    image: ImageName;
    imageAlt: string;
  };

  /** Label of the tab on /services ("Residential Sanctuaries"). */
  tabLabel: string;
  /** Discipline line, rendered as "Discipline 01 // Private Sanctuaries". */
  discipline: { number: string; label: string; icon: IconName };
  /** The detail page <h1>: main keyword + location. */
  h1: string;
  /** Editorial headline (the design's discipline title). */
  headline: string;
  /** Discipline paragraph (shown on /services and the detail page). */
  intro: string;
  /** Exactly two short scope/execution facts. */
  facts: [ServiceFact, ServiceFact];
  /** "Included Architectural Deliverables". */
  deliverables: string[];
  image: ImageName;
  imageAlt: string;
  /** Overlay card on the discipline image. `project` links it to a case study. */
  featured: { label: string; title: string; icon: IconName; project?: string };
  /** Call to action text on /services (links to /contact). */
  briefCta: string;

  /** Detail page content. */
  detail: {
    /** Hero lead paragraph. */
    lead: string;
    /** Extra overview paragraphs after the intro. */
    overview: string[];
    /** "Spaces we design" cards. */
    spaces: Array<{ title: string; text: string }>;
    /** Related case studies (slugs from data/projects.ts). */
    relatedProjects: string[];
    faqs: ServiceFaq[];
  };

  seo: {
    /** Page topic (~25–35 chars); " | Green Valley Interiors" is appended. */
    title: string;
    /** 140–160 characters. */
    description: string;
    /** 5–8 long-tail phrases specific to this service. */
    keywords: string[];
  };
};

// NOTE: placeholder content from the Stitch design (headline, intro, facts, deliverables,
// featured case names). The detail-page copy is new and avoids hard facts. Slugs keep the
// previous site's /services/residential-design URLs so existing links and rankings carry over.
export const services: Service[] = [
  {
    slug: 'residential-design',
    name: 'Residential Interior Design',
    icon: 'chair',
    homeCard: {
      badge: '01 • Living Sanctuaries',
      title: 'Residential Design',
      description:
        'Transform your residence into a masterpiece of poise, tactile comfort, and intuitive layout. Custom millwork, ambient lighting schemas, and personalized master suites.',
      highlights: ['Modular Kitchen Concepts', 'Classical Villa Interiors', 'Spatial Renovation & Styling'],
      image: 'home-service-residential',
      imageAlt: 'Contemporary residential living room with custom wall panelling, linen sofas and a silk rug',
    },
    tabLabel: 'Residential Sanctuaries',
    discipline: { number: '01', label: 'Private Sanctuaries', icon: 'home_pin' },
    h1: 'Residential Interior Design in Nagercoil',
    headline: 'Private Residential Architecture & Master Spatial Renovations',
    intro:
      'We design private sanctuaries that evoke meditative tranquility. From high-ceilinged double-height penthouses to coastal villas, our residential masterworks integrate acoustic isolation, bespoke climate niches, and integrated concealed joinery.',
    facts: [
      { label: 'Scope Spectrum', value: 'Master suites, private wine cellars, custom chef galleys, & indoor courtyards' },
      { label: 'Execution Window', value: '14 to 28 Weeks depending on structural scope' },
    ],
    deliverables: [
      'Complete MEP & HVAC Schematics',
      'Custom Micro-Cement Finishes',
      'Integrated Smart Lighting Layers',
      'Turnkey Site Supervision',
    ],
    image: 'services-residential-living',
    imageAlt:
      'Double-height living room with travertine cladding, an emerald velvet sofa, brass fixtures and garden views',
    featured: { label: 'Featured Case Ref.', title: 'The Palladian Villa, Bengaluru', icon: 'architecture' },
    briefCta: 'Commission Residential Brief',
    detail: {
      lead: 'Villas, independent houses and apartments across Nagercoil, Kanyakumari and Trivandrum, planned around the way your family lives and finished by one accountable team from first sketch to handover.',
      overview: [
        'Every residential commission starts with how you use the house: who cooks, where the family gathers in the evening, which rooms need to stay cool through the afternoon and where guests are received. We turn that brief into zoning plans, furniture layouts and lighting schemes before any material is chosen.',
        'Homes along the coast face heat, humidity and salt air for most of the year, so we favour cross-ventilation, moisture-resistant carcasses, seasoned timber and finishes that are easy to maintain. Joinery, false ceilings, electrical and plumbing are coordinated in one set of drawings, so the site team builds exactly what you approved.',
      ],
      spaces: [
        {
          title: 'Living & Family Rooms',
          text: 'Layouts that balance conversation, media and daylight, with wall panelling, storage and layered lighting planned together.',
        },
        {
          title: 'Master Suites & Bedrooms',
          text: 'Calm, well-ventilated bedrooms with wardrobes, dressing areas and bedside lighting designed to the inch.',
        },
        {
          title: 'Kitchens & Dining',
          text: 'Chef galleys and modular kitchens that connect naturally to the dining table, pantry and utility.',
        },
        {
          title: 'Villa & Full-Home Renovations',
          text: 'Complete turnkey renovations, from civil changes and services to joinery, styling and final handover.',
        },
      ],
      relatedProjects: ['residence-at-palm-grove', 'minimalist-primary-suite', 'classic-villa-interiors'],
      faqs: [
        {
          question: 'Do you design complete homes or individual rooms?',
          answer:
            'Both. We take on full turnkey villas and apartments, and focused projects such as a master suite, living room or kitchen. The process is the same: survey, design and 3D visualisation, then execution with our own joinery team.',
        },
        {
          question: 'How long does a residential interior project take?',
          answer:
            'Most residential projects run 14 to 28 weeks depending on structural scope. We share a milestone schedule after the design is approved so you know when each stage will be finished.',
        },
        {
          question: 'Which areas do you serve for home interiors?',
          answer:
            'Our studio is in Nagercoil and we regularly work across Kanyakumari district, Trivandrum, Tirunelveli and the wider Tamil Nadu and Kerala region. Get in touch to discuss your location.',
        },
        {
          question: 'How do you plan interiors for the humid coastal climate?',
          answer:
            'We plan for airflow and daylight first, then specify moisture-resistant boards, seasoned timber, sealed stone and washable finishes, and we leave ventilation gaps behind wardrobes and kitchen units to avoid damp and mould.',
        },
      ],
    },
    seo: {
      title: 'Home Interior Design in Nagercoil',
      description:
        'Residential interior design for villas, homes and apartments in Nagercoil, Kanyakumari and Trivandrum. Turnkey design, custom joinery and site supervision.',
      keywords: [
        'residential interior designers in Nagercoil',
        'villa interior design in Kanyakumari',
        'apartment interior design in Trivandrum',
        'turnkey home interiors in Nagercoil',
        'master bedroom interior design Nagercoil',
        'full home renovation interior designers Tamil Nadu',
      ],
    },
  },
  {
    slug: 'office-design',
    name: 'Office & Corporate Interiors',
    icon: 'corporate_fare',
    homeCard: {
      badge: '02 • Productive Elegance',
      title: 'Office & Corporate',
      description:
        'We shape corporate suites that foster deep focus and seamless collaboration, tailored to mirror brand prestige. Ergonomic layouts, acoustic balances, and boardrooms.',
      highlights: ['Executive Chambers & Suites', 'Ergonomic Collaborative Hubs', 'Acoustic & Lighting Engineering'],
      image: 'home-service-office',
      imageAlt: 'Executive office with a walnut desk, slatted timber acoustic panels and glass partitions',
    },
    tabLabel: 'Workspaces',
    discipline: { number: '02', label: 'Executive Environments', icon: 'domain' },
    h1: 'Office Interior Design in Nagercoil',
    headline: 'Executive Head Offices, Boardrooms & High-Output Studios',
    intro:
      'We replace antiseptic cubicles with elevated corporate sanctuaries. Combining scientific acoustic damping with aristocratic boardroom architecture, our spaces inspire decisive leadership and seamless collaboration.',
    facts: [
      { label: 'Acoustic Standards', value: 'NRC 0.85+ calibrated sound isolation partitions' },
      { label: 'Execution Window', value: '10 to 18 Weeks with zero office downtime staging' },
    ],
    deliverables: [
      'Biophilic Air Purification Niches',
      'Custom Walnut Executive Desks',
      'Concealed Conference Telemetry',
      'Circadian LED Architecture',
    ],
    image: 'services-corporate-boardroom',
    imageAlt:
      'Corporate boardroom with fluted oak wall panelling, a dark quartz conference table and leather executive chairs',
    featured: { label: 'Featured Case Ref.', title: 'Apex Capital Headquarters, Chennai', icon: 'business_center' },
    briefCta: 'Request Corporate Presentation',
    detail: {
      lead: 'Head offices, clinics, boardrooms and studios for growing businesses in Nagercoil and South Tamil Nadu, planned for focus, collaboration and a brand impression that clients remember.',
      overview: [
        'We begin with how your teams work: who needs quiet, who collaborates, how visitors move from reception to meeting rooms, and how much the business expects to grow. That brief drives the space plan, furniture layout, cabling and lighting.',
        'Acoustic panelling, glass partitions and ergonomic workstations are detailed alongside data, power and air-conditioning, so the office is comfortable through long working days. Where you need to keep working during the fit-out, we phase the works to limit disruption.',
      ],
      spaces: [
        {
          title: 'Executive Cabins',
          text: 'Private offices with custom desks, storage and meeting corners that reflect the standing of the business.',
        },
        {
          title: 'Boardrooms & Meeting Rooms',
          text: 'Acoustically treated rooms with concealed cabling, presentation walls and comfortable, adjustable lighting.',
        },
        {
          title: 'Open Workspaces',
          text: 'Ergonomic workstations, collaboration zones and quiet pods arranged for daylight and easy circulation.',
        },
        {
          title: 'Receptions & Client Lounges',
          text: 'Welcoming first impressions with brand-led materials, signage, seating and lighting.',
        },
      ],
      relatedProjects: ['pinnacle-corporate-boardroom', 'executive-office-space'],
      faqs: [
        {
          question: 'Can you design our office while we keep working?',
          answer:
            'Yes. We plan the works in phases and stage the site so teams can keep operating, typically completing office projects in 10 to 18 weeks depending on scope.',
        },
        {
          question: 'Do you handle acoustics, lighting and cabling?',
          answer:
            'Yes. Acoustic panelling and partitions, lighting design, data and power points and air-conditioning coordination are all part of our office drawings, so nothing is left to be resolved on site.',
        },
        {
          question: 'What size of office do you take on?',
          answer:
            'We design single executive cabins, boardrooms and complete head offices. Share your floor area and headcount, and we will suggest the right scope for your budget.',
        },
      ],
    },
    seo: {
      title: 'Office Interior Design in Nagercoil',
      description:
        'Office interior design in Nagercoil for head offices, boardrooms and executive cabins: acoustic panelling, ergonomic layouts, lighting and turnkey fit-out.',
      keywords: [
        'office interior designers in Nagercoil',
        'corporate office interior design Tamil Nadu',
        'boardroom interior design in Nagercoil',
        'executive cabin interior design Kanyakumari',
        'turnkey office fit-out in Trivandrum',
        'acoustic office interiors for small businesses',
      ],
    },
  },
  {
    slug: 'commercial-design',
    name: 'Commercial & Hospitality Interiors',
    icon: 'storefront',
    homeCard: {
      badge: '03 • Commercial Destination',
      title: 'Commercial & Luxury',
      description:
        'Transform retail stores, restaurants, boutique hospitality spaces, and flagship showrooms into immersive brand destinations that captivate high-discretion clientele.',
      highlights: ['Experiential Flagship Boutiques', 'Fine Dining & Lounge Concepts', 'Luxury Real Estate Staging'],
      image: 'home-service-commercial',
      imageAlt: 'Restaurant lounge with curved plaster arches, velvet booths and a brass chandelier',
    },
    tabLabel: 'Hospitality Havens',
    discipline: { number: '03', label: 'Experiential Venues', icon: 'restaurant' },
    h1: 'Commercial & Hospitality Interior Design in Nagercoil',
    headline: 'Boutique Luxury Resorts, Fine Dining & Flagship Retail',
    intro:
      'Curating environments that command emotional memory. We sculpt destination restaurants, tasting salons, and premier boutique hotels where lighting choreography, material richness, and spatial circulation create magnetic guest journeys.',
    facts: [
      { label: 'Atmospheric Index', value: 'Sensory material layers & spatial storytelling' },
      { label: 'Execution Window', value: '16 to 36 Weeks tailored to grand opening schedules' },
    ],
    deliverables: [
      'Signature Back-lit Onyx Bars',
      'High-traffic Resilient Luxury Textiles',
      'Comprehensive Guest Wayfinding',
      'Compliance & Fire Life-Safety Integration',
    ],
    image: 'services-boutique-restaurant',
    imageAlt:
      'Boutique restaurant with curved emerald velvet booths, fluted timber screens and warm brass pendant lights',
    featured: { label: 'Featured Case Ref.', title: 'Verdant Grove Retreat, Munnar', icon: 'local_bar' },
    briefCta: 'Explore Hospitality Portfolio',
    detail: {
      lead: 'Restaurants, cafés, boutique hotels, showrooms and retail stores across Nagercoil, Kanyakumari and Kerala, designed to draw guests in, move them comfortably and bring them back.',
      overview: [
        'Commercial interiors have to work hard: they carry your brand, handle heavy daily use and must open on time. We start from the guest journey and the operational flow — kitchen to table, stockroom to shop floor, check-in to room — and design the space around both.',
        'Lighting scenes, durable finishes, signage and wayfinding are specified together with services and safety requirements. For hospitality projects in coastal towns and hill stations, we choose upholstery, stone and metalwork that stand up to humidity and constant footfall.',
      ],
      spaces: [
        {
          title: 'Restaurants & Cafés',
          text: 'Dining rooms, bars and lounges with seating plans, lighting and acoustics tuned for atmosphere and turnover.',
        },
        {
          title: 'Boutique Hotels & Resorts',
          text: 'Lobbies, guest rooms and suites that carry a consistent story from arrival to check-out.',
        },
        {
          title: 'Retail & Showrooms',
          text: 'Flagship stores and showrooms with display joinery, circulation and lighting that put the product first.',
        },
        {
          title: 'Clinics & Studios',
          text: 'Calm, practical client-facing spaces with easy-clean finishes and clear wayfinding.',
        },
      ],
      relatedProjects: ['gourmet-dining-atelier', 'heritage-manor-dining', 'pinnacle-corporate-boardroom'],
      faqs: [
        {
          question: 'Can you work to a fixed opening date?',
          answer:
            'Yes. Commercial and hospitality projects typically run 16 to 36 weeks, and we build the programme backwards from your opening date, with joinery made in our workshop while site works progress.',
        },
        {
          question: 'Do you design restaurant kitchens and bars?',
          answer:
            'We design the front-of-house, bars and service counters and coordinate the layout of the commercial kitchen with your chef and equipment supplier so that service flows smoothly.',
        },
        {
          question: 'How do you choose finishes for high footfall?',
          answer:
            'We specify commercial-grade upholstery, sealed stone, hard-wearing laminates and solid timber edges, and we detail joinery so worn parts can be repaired or replaced without rebuilding.',
        },
      ],
    },
    seo: {
      title: 'Commercial Interiors in Nagercoil',
      description:
        'Commercial and hospitality interior design for restaurants, cafés, boutique hotels and showrooms in Nagercoil, Kanyakumari and Kerala, from concept to opening.',
      keywords: [
        'restaurant interior designers in Nagercoil',
        'commercial interior design in Kanyakumari',
        'boutique hotel interior design in Kerala',
        'cafe interior design in Nagercoil',
        'retail showroom interior designers Tamil Nadu',
        'hospitality interior design company near Trivandrum',
      ],
    },
  },
  {
    slug: 'modular-kitchen-design',
    name: 'Modular Kitchen Design',
    icon: 'countertops',
    tabLabel: 'Haute Kitchens',
    discipline: { number: '04', label: 'Culinary Galleys', icon: 'countertops' },
    h1: 'Modular Kitchen Design in Nagercoil',
    headline: 'Haute Modular Kitchens, Chef Galleys & Integrated Millwork',
    intro:
      'The kitchen is the working heart of the home. We plan modular kitchens around how your family really cooks, with ergonomic work zones, tall pantry towers and concealed appliance garages, then build them in our own atelier with precision hardware and stone worktops.',
    facts: [
      { label: 'Layout Spectrum', value: 'Parallel, L-shaped, U-shaped & island kitchens, pantries and utilities' },
      { label: 'Material Focus', value: 'Moisture-resistant carcasses, quartz & quartzite worktops' },
    ],
    deliverables: [
      'Ergonomic Work-Zone Planning',
      'Tall Pantry & Appliance Integration',
      'Soft-Close Concealed German Hardware',
      'Task & Under-Cabinet Lighting',
    ],
    image: 'project-modular-kitchen',
    imageAlt: 'Modern modular kitchen with emerald green cabinetry and a white quartz waterfall island',
    featured: {
      label: 'Featured Case Ref.',
      title: 'High-Precision Kitchen, Madurai',
      icon: 'countertops',
      project: 'high-precision-kitchen',
    },
    briefCta: 'Plan Your Kitchen Brief',
    detail: {
      lead: 'Made-to-measure modular kitchens for homes in Nagercoil, Kanyakumari and Trivandrum, designed around the way you cook and built in our own workshop to last in a hot, humid climate.',
      overview: [
        'We start with a site measure and a conversation about your cooking: daily meals or occasional entertaining, traditional grinding and frying or quick weekday cooking, one cook or several. From that we plan the layout, the sink, hob and refrigerator positions, and storage for every utensil and appliance.',
        'Kitchens in coastal Tamil Nadu and Kerala face steam, heat and humidity every day, so we specify moisture-resistant boards, sealed edges, stainless steel or coated fittings, and worktops that resist stains from turmeric and oil. Chimney ducting, plumbing and electrical points are drawn before the cabinets are made.',
      ],
      spaces: [
        {
          title: 'Parallel & L-Shaped Kitchens',
          text: 'Efficient layouts for apartments and compact homes that keep everything within a step or two.',
        },
        {
          title: 'Island & Open Kitchens',
          text: 'Islands and breakfast counters that connect the kitchen to the dining and family areas.',
        },
        {
          title: 'Wet & Dry Kitchens',
          text: 'A separate work kitchen for heavy cooking alongside a clean display kitchen for serving and guests.',
        },
        {
          title: 'Pantries & Utilities',
          text: 'Tall larder units, appliance garages and laundry utilities that keep clutter off the worktop.',
        },
      ],
      relatedProjects: ['high-precision-kitchen', 'modular-kitchen-concepts', 'heritage-manor-dining'],
      faqs: [
        {
          question: 'Which kitchen layout is right for my home?',
          answer:
            'It depends on the room shape, door and window positions and how many people cook together. Parallel and L-shaped layouts suit most apartments, while U-shaped and island kitchens work well in larger villas. We recommend a layout after measuring the space.',
        },
        {
          question: 'What materials hold up best in a humid climate?',
          answer:
            'We use moisture-resistant boards with sealed edges for carcasses, durable laminate, acrylic or lacquered shutters, and quartz or quartzite worktops. Hardware is chosen for smooth soft-close movement and resistance to corrosion.',
        },
        {
          question: 'Can you plan a separate wet kitchen?',
          answer:
            'Yes. Many families prefer a work kitchen for heavy cooking and a clean show kitchen for serving. We plan both together so ventilation, storage and plumbing work for each.',
        },
        {
          question: 'Do you include appliances and lighting?',
          answer:
            'We plan built-in appliances, chimney ducting and task and under-cabinet lighting as part of the design, and can help you select appliances that fit the cabinetry.',
        },
      ],
    },
    seo: {
      title: 'Modular Kitchen Design, Nagercoil',
      description:
        'Modular kitchen design in Nagercoil: parallel, L-shaped, U-shaped and island kitchens built in our own workshop with moisture-resistant materials and hardware.',
      keywords: [
        'modular kitchen designers in Nagercoil',
        'modular kitchen design in Kanyakumari',
        'island kitchen design for villas Tamil Nadu',
        'L-shaped modular kitchen in Nagercoil',
        'wet and dry kitchen design Kerala homes',
        'custom kitchen cabinets near Trivandrum',
      ],
    },
  },
  {
    slug: 'bespoke-joinery',
    name: 'Bespoke Joinery & Millwork',
    icon: 'carpenter',
    tabLabel: 'Artisanal Joinery',
    discipline: { number: '05', label: 'Artisanal Fabrication', icon: 'carpenter' },
    h1: 'Bespoke Joinery & Custom Furniture in Nagercoil',
    headline: 'Bespoke Joinery, Bookmatched Marbles & Global Procurement',
    intro:
      'True luxury lives within millimetric tolerances. Operating our own artisanal joinery workshops, we craft custom credenzas, library armoires, architectural wall panels, and personally source rare Italian stones and European hard woods.',
    facts: [
      { label: 'Woodwork Precision', value: '0.5mm German joinery tolerances' },
      { label: 'Direct Sourcing', value: 'Quarries in Carrara, Verona, and Rajasthan' },
    ],
    deliverables: [
      'Solid Teak & American Walnut Milling',
      'Soft-Close Concealed German Hardware',
      'Anti-Warp Seasoned Timber Treatment',
      'White-Glove Installation with Guild Artisans',
    ],
    image: 'services-walnut-joinery',
    imageAlt:
      'Bespoke walnut joinery with champagne brass hardware, a bookmatched marble countertop and warm cove lighting',
    featured: { label: 'Workshop Specialization', title: 'Atelier Joinery Division • Nagercoil', icon: 'handyman' },
    briefCta: 'View Material Provenance Catalog',
    detail: {
      lead: 'Wardrobes, wall panelling, credenzas, libraries and statement furniture made to measure in our Nagercoil atelier, in seasoned timber and stone chosen for each project.',
      overview: [
        'Off-the-shelf furniture rarely fits the walls, ceiling heights and storage needs of a real home or office. Our joiners work from the same drawings as the designers, so every wardrobe, panel and cabinet fits its space exactly and lines up with lighting, switches and air-conditioning.',
        'Timber is seasoned and treated before it is cut, joints and edges are sealed against humidity, and hardware is selected for smooth, long-lasting movement. Pieces are pre-assembled and checked in the workshop before our installation team fits them on site.',
      ],
      spaces: [
        {
          title: 'Wardrobes & Walk-in Closets',
          text: 'Floor-to-ceiling storage with internal organisers, lighting and finishes matched to the bedroom.',
        },
        {
          title: 'Wall Panelling & TV Units',
          text: 'Fluted, slatted and veneered panels and media walls that conceal cables and speakers.',
        },
        {
          title: 'Libraries & Study Furniture',
          text: 'Bookcases, desks and window seats built into alcoves and around architectural features.',
        },
        {
          title: 'Statement Furniture',
          text: 'Credenzas, dining tables, consoles and vanities designed for one specific room.',
        },
      ],
      relatedProjects: ['heritage-manor-dining', 'pinnacle-corporate-boardroom', 'high-precision-kitchen'],
      faqs: [
        {
          question: 'Do you make furniture in your own workshop?',
          answer:
            'Yes. Our joinery is made in our own atelier in Nagercoil, which lets us control the timber, the finish and the fit, and coordinate installation directly with the site team.',
        },
        {
          question: 'Can I order custom joinery without a full interior project?',
          answer:
            'Yes. We take on standalone pieces such as wardrobes, wall panelling, libraries and dining tables. We measure the space, share drawings and finish samples, and build once you approve.',
        },
        {
          question: 'How do you stop wood from warping in humid weather?',
          answer:
            'We use seasoned and treated timber, moisture-resistant boards for carcasses, sealed edges and finishes, and we leave ventilation gaps behind units fixed to external walls.',
        },
      ],
    },
    seo: {
      title: 'Bespoke Joinery in Nagercoil',
      description:
        'Bespoke joinery and custom furniture made in our Nagercoil workshop: wardrobes, wall panelling, libraries and credenzas in seasoned teak, walnut and stone.',
      keywords: [
        'custom furniture makers in Nagercoil',
        'bespoke wardrobe design in Kanyakumari',
        'teak wood joinery workshop Tamil Nadu',
        'wall panelling and TV unit design Nagercoil',
        'made to measure furniture near Trivandrum',
        'custom library and bookshelf joinery',
      ],
    },
  },
];

export const getService = (slug: string) => services.find((s) => s.slug === slug);

/** "The 4-Step Execution Trajectory" (shown on /services and every service page). */
export const processSteps: Array<{ number: string; phase: string; title: string; text: string; output: string }> = [
  {
    number: '01',
    phase: 'Initiation',
    title: 'Spatial Cartography & Feasibility',
    text: 'Laser volumetric surveying, acoustic diagnostics, and lifestyle telemetry analysis to build the ironclad spatial brief.',
    output: 'Schematic Zoning & Feasibility Matrix',
  },
  {
    number: '02',
    phase: 'Visualization',
    title: '3D Metamorphosis & VR Renders',
    text: 'Photorealistic optical renders under simulated solar paths, comprehensive material moodboards, and structural sign-off.',
    output: 'Full Architectural BIM & Virtual Walkthrough',
  },
  {
    number: '03',
    phase: 'Crafting',
    title: 'Material Provenance & Millwork',
    text: 'Raw teak seasoning, CNC cutting of joinery elements in our atelier, custom stone selection, and on-site MEP conduit deployment.',
    output: 'Pre-assembled Joinery & Quarry Verification',
  },
  {
    number: '04',
    phase: 'Delivery',
    title: 'Turnkey White-Glove Handover',
    text: 'Acoustic calibration, deep electrostatic sanitization, artwork curatorial hanging, and presentation of the Leather Atelier Dossier.',
    output: 'Habitation Ready & 18-Month Care Warranty',
  },
];

/** "Transparent Scope & Tiers" on /services. NOTE: placeholder prices from the design — verify. */
export const pricingTiers: Array<{
  badge: string;
  title: string;
  text: string;
  price: string;
  priceNote: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
}> = [
  {
    badge: 'Targeted Scope',
    title: 'Executive Sanctum',
    text: 'Focused transformation for single high-priority chambers: master suites, corner executive offices, or bespoke home cinemas.',
    price: '₹12L+',
    priceNote: '/ Space Starting Baseline',
    features: [
      'Custom acoustic fluted oak wall paneling',
      'Lighting scheme & architectural cove automation',
      'Atelier custom vanity or executive desk fabrication',
      'Curated designer lighting & soft furnishing curation',
    ],
    cta: 'Initiate Single Sanctum',
  },
  {
    badge: 'Full Residential',
    title: 'Bespoke Estate Villa',
    text: 'Comprehensive turnkey architectural rejuvenation for upscale apartments, independent villas, and expansive family estates.',
    price: '₹35L – ₹85L',
    priceNote: '/ Typical Curated Scope',
    features: [
      'Complete interior architectural overhaul & civil restructuring',
      'Full turnkey atelier millwork (Gourmet kitchen + all bedrooms)',
      'Imported bookmatched marble flooring & bath sanctuaries',
      'Full MEP, automation, and dedicated Resident Project Architect',
      '18-Month White-Glove Guarantee & maintenance retainer',
    ],
    cta: 'Reserve Villa Consultation',
    highlighted: true,
  },
  {
    badge: 'Commercial / Hospitality',
    title: 'Commercial Flagship',
    text: 'Unrestricted custom execution for signature hotels, experiential retail, fine-dining restaurants, and corporate board facilities.',
    price: 'Bespoke Scope',
    priceNote: '/ Architectural Tender',
    features: [
      'Multi-level circulation & specialized building code compliance',
      'High-durability luxury finishes and sound isolation engineering',
      'Custom lighting matrix with day-to-night scene transitions',
      'Dedicated procurement agent for global vintage and custom pieces',
    ],
    cta: 'Inquire Corporate Tender',
  },
];

/** "Materials of Noble Lineage" grid on /services. */
export const materials: Array<{ kind: string; name: string; image: ImageName; alt: string }> = [
  {
    kind: 'Stone',
    name: 'Verde Quartzite',
    image: 'material-emerald-quartzite',
    alt: 'Polished green quartzite with white and gold veining',
  },
  {
    kind: 'Timber',
    name: 'Kiln-Dried Nilgiri Teak',
    image: 'material-teak',
    alt: 'Seasoned dark teak timber grain with a satin oil finish',
  },
  {
    kind: 'Metal',
    name: 'Aged Patinated Brass',
    image: 'material-brass',
    alt: 'Hand-brushed champagne brass hardware with fine machining lines',
  },
  {
    kind: 'Coating',
    name: 'Venetian Microcement',
    image: 'material-microcement',
    alt: 'Hand-trowelled ivory microcement wall with soft surface ripples',
  },
  {
    kind: 'Textile',
    name: 'Raw Belgian Linen',
    image: 'material-linen-silk',
    alt: 'Woven linen and raw silk upholstery fabric in sage and oatmeal tones',
  },
  {
    kind: 'Glazing',
    name: 'Reeded Acoustic Glazing',
    image: 'material-reeded-glass',
    alt: 'Backlit fluted reeded glass panel in a dark bronze frame',
  },
];
